import {
  inputContainerClass,
  inputMinimalClass,
} from '@w/ui/components/input'
import { cn } from '@w/ui/lib/utils'
import { useRef, useState } from 'react'

const LINE_BREAK = '\u{E000}'

type TrainingTextInputProps = {
  'aria-label': string
  autoFocus?: boolean
  className?: string
  'data-answer-result'?: 'correct' | 'wrong'
  'data-testid': string
  disabled?: boolean
  minimal?: boolean
  multiline?: boolean
  placeholder?: string
  value: string
  onValueChange: (value: string) => void
  /** Single-line only: Enter on the input (multiline Enter inserts a line break). */
  onEnter?: () => void
}

/**
 * A real password input requests secure mobile-keyboard behavior. Its one-time
 * code autocomplete hint keeps Chrome from treating each answer as a saved
 * credential. Native masked text stays transparent while a mirror renders it.
 * Multiline answers encode line breaks because HTML inputs are single-line.
 */
export function TrainingTextInput({
  'aria-label': ariaLabel,
  autoFocus,
  className,
  'data-answer-result': answerResult,
  'data-testid': testId,
  disabled,
  minimal,
  multiline = false,
  placeholder,
  value,
  onValueChange,
  onEnter,
}: TrainingTextInputProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [focused, setFocused] = useState(false)
  const [selection, setSelection] = useState({
    start: value.length,
    end: value.length,
  })
  const encodedValue = multiline ? _encode(value) : value

  const syncSelection = () => {
    const input = inputRef.current
    if (!input) return
    setSelection({
      start: input.selectionStart ?? input.value.length,
      end: input.selectionEnd ?? input.value.length,
    })
  }

  const replaceSelection = (insertedValue: string) => {
    const input = inputRef.current
    const currentValue = input?.value ?? encodedValue
    const start = input?.selectionStart ?? currentValue.length
    const end = input?.selectionEnd ?? currentValue.length
    const encodedInsert = multiline ? _encode(insertedValue) : insertedValue
    const nextValue = `${currentValue.slice(0, start)}${encodedInsert}${currentValue.slice(end)}`
    const nextCaret = start + encodedInsert.length
    onValueChange(multiline ? _decode(nextValue) : nextValue)
    queueMicrotask(() => {
      inputRef.current?.setSelectionRange(nextCaret, nextCaret)
      setSelection({ start: nextCaret, end: nextCaret })
    })
  }

  return (
    <span
      className={cn(
        minimal ? inputMinimalClass : inputContainerClass,
        multiline ? 'items-start py-4 pr-0' : 'items-center',
        className,
      )}
      data-answer-result={answerResult}
      data-slot="input-control"
    >
      <input
        ref={inputRef}
        aria-label={ariaLabel}
        aria-multiline={multiline ? 'true' : undefined}
        autoCapitalize="none"
        autoComplete="one-time-code"
        autoCorrect="off"
        // oxlint-disable-next-line eslint-plugin-jsx-a11y/no-autofocus -- training answer is page's only focus target
        autoFocus={autoFocus}
        className="absolute inset-0 z-10 size-full cursor-text opacity-0"
        data-answer-result={answerResult}
        data-testid={testId}
        disabled={disabled}
        enterKeyHint={multiline ? 'enter' : undefined}
        placeholder={placeholder}
        spellCheck={false}
        type="password"
        value={focused && !disabled ? encodedValue : ''}
        onBeforeInput={(event) => {
          if (!multiline) return
          if (
            event.nativeEvent.inputType !== 'insertLineBreak' &&
            event.nativeEvent.inputType !== 'insertParagraph'
          ) {
            return
          }
          event.preventDefault()
          replaceSelection('\n')
        }}
        onInput={(event) => {
          onValueChange(
            multiline
              ? _decode(event.currentTarget.value)
              : event.currentTarget.value,
          )
          syncSelection()
        }}
        onClick={syncSelection}
        onFocus={() => {
          setFocused(true)
          queueMicrotask(() => {
            const input = inputRef.current
            if (!input) return
            const start = Math.min(selection.start, input.value.length)
            const end = Math.min(selection.end, input.value.length)
            input.setSelectionRange(start, end)
            setSelection({ start, end })
          })
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return
          if (!multiline) {
            if (onEnter) { event.preventDefault(); onEnter() }
            return
          }
          event.preventDefault()
          replaceSelection('\n')
        }}
        onKeyUp={syncSelection}
        onPaste={(event) => {
          if (!multiline) return
          event.preventDefault()
          replaceSelection(event.clipboardData.getData('text'))
        }}
        onSelect={syncSelection}
        onBlur={() => {
          syncSelection()
          setFocused(false)
        }}
      />
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none block min-h-[1lh] w-full leading-none',
          multiline
            ? 'pr-4 whitespace-pre-wrap break-words'
            : 'overflow-hidden whitespace-pre',
        )}
        data-testid={`${testId}-mirror`}
      >
        <MirroredValue
          focused={focused}
          placeholder={placeholder}
          selection={selection}
          value={value}
        />
      </span>
    </span>
  )
}

function MirroredValue({
  focused,
  placeholder,
  selection,
  value,
}: {
  focused: boolean
  placeholder?: string
  selection: { start: number; end: number }
  value: string
}) {
  if (!value) {
    return (
      <>
        {focused && <MirrorCaret />}
        <span className="text-on-surface-secondary select-none">
          {placeholder}
        </span>
      </>
    )
  }

  if (!focused) return value

  const start = Math.min(selection.start, value.length)
  const end = Math.min(selection.end, value.length)
  return (
    <>
      {value.slice(0, start)}
      {start === end ? (
        <MirrorCaret />
      ) : (
        <span className="bg-primary text-on-primary">
          {value.slice(start, end)}
        </span>
      )}
      {value.slice(end)}
    </>
  )
}

function MirrorCaret() {
  return (
    <span
      className="border-on-surface inline-block h-[1em] border-l align-text-bottom"
      data-training-mirror-caret="true"
    />
  )
}

function _encode(value: string) {
  return value.replaceAll('\n', LINE_BREAK)
}

function _decode(value: string) {
  return value.replaceAll(LINE_BREAK, '\n')
}
